import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useMatchSocket } from "@/lib/useMatchSocket";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Play, Pause, Undo2, Plus, Minus, Star, Hand, Zap, Users } from "lucide-react";
import { fmtTime, photoUrl } from "@/lib/format";
import { toast } from "sonner";
import MatchCommentary from "@/components/MatchCommentary";

export default function UmpirePanel() {
  const { matchId } = useParams();
  const nav = useNavigate();
  const { state: m } = useMatchSocket(matchId);
  const [matches, setMatches] = useState([]);
  const [teamRosters, setTeamRosters] = useState({ a: [], b: [] });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/matches");
        setMatches(data);
        if (!matchId && data[0]) nav(`/umpire/${data[0].id}`, { replace: true });
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, [matchId, nav]);

  useEffect(() => {
    if (!m) return;
    (async () => {
      const ids = [m.team_a_id, m.team_b_id].filter(Boolean);
      if (!ids.length) return;
      const { data: teams } = await api.get("/teams");
      const ta = teams.find((t) => t.id === m.team_a_id);
      const tb = teams.find((t) => t.id === m.team_b_id);
      setTeamRosters({ a: ta?.players || [], b: tb?.players || [] });
    })();
  }, [m?.team_a_id, m?.team_b_id]);

  const act = async (path, body) => {
    try {
      await api.post(`/matches/${matchId}/${path}`, body || {});
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!matchId) return <div className="p-8 text-zinc-400" data-testid="umpire-empty">Select a match.</div>;
  if (!m) return <div className="p-8 text-zinc-500" data-testid="umpire-loading">Loading match…</div>;

  const cfg = m.config_snapshot || {};
  const periodElapsed = cfg.period_seconds - (m.period_remaining || 0);
  const lockedSubs = periodElapsed < cfg.lockin_seconds || periodElapsed >= cfg.tapout_cutoff_seconds;

  return (
    <div className="p-4 md:p-6">
      {/* Match selector */}
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Active Match:</span>
        <select
          data-testid="umpire-match-select"
          value={matchId}
          onChange={(e) => nav(`/umpire/${e.target.value}`)}
          className="bg-[#11241b] border border-white/10 rounded-md px-3 py-1.5 text-sm text-white"
        >
          {matches.map((mm) => {
            const label = `${mm.court || ""}: ${mm.team_a?.name || "TBD"} vs ${mm.team_b?.name || "TBD"}`;
            return <option key={mm.id} value={mm.id}>{label}</option>;
          })}
        </select>
        <span className="text-xs text-zinc-500">·  Status: <span className="text-white">{m.status}</span> {m.running ? "(running)" : "(paused)"}</span>
      </div>

      {/* Top stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Stat label="Match Clock" value={fmtTime(m.match_remaining)} accent />
        <Stat label={`Period ${m.current_period}/${cfg.num_periods}`} value={fmtTime(m.period_remaining)} />
        <Stat label="Shot Clock" value={fmtTime(m.shot_clock_remaining)} danger={m.shot_clock_remaining < 4} />
        <Stat label="Tap-Out" value={m.tapout_active ? fmtTime(m.tapout_remaining) : "—"} amber={m.tapout_active} />
        <Stat label="Service" value={`Team ${m.service_team?.toUpperCase()} (${m.service_point_counter})`} />
      </div>

      {/* Control row */}
      <div className="flex flex-wrap gap-2 mb-4">
        {m.status === "scheduled" && (
          <Button data-testid="btn-start" onClick={() => act("start")} className="bg-[#00A86B] hover:bg-[#00935A] text-white"><Play className="w-4 h-4 mr-1.5" /> Start Match</Button>
        )}
        {m.status === "live" && (
          m.running
            ? <Button data-testid="btn-pause" onClick={() => act("pause")} variant="outline" className="border-white/20"><Pause className="w-4 h-4 mr-1.5" /> Pause</Button>
            : <Button data-testid="btn-resume" onClick={() => act("resume")} className="bg-[#00A86B] hover:bg-[#00935A] text-white"><Play className="w-4 h-4 mr-1.5" /> Resume</Button>
        )}
        <Button data-testid="btn-undo" onClick={() => act("undo")} variant="outline" className="border-white/20 text-white">
          <Undo2 className="w-4 h-4 mr-1.5" /> Undo Last Point
        </Button>
        <Button data-testid="btn-reset-shot-clock" variant="outline" className="border-white/20 text-white" onClick={() => act("shot-clock", { reset: true })}>
          Reset Shot Clock
        </Button>
      </div>

      {/* Team Controls */}
      <div className="grid md:grid-cols-2 gap-4">
        <TeamControls side="a" team={m.team_a || { name: "Team A" }} score={m.score_a} act={act} cfg={cfg} m={m} lockedSubs={lockedSubs} />
        <TeamControls side="b" team={m.team_b || { name: "Team B" }} score={m.score_b} act={act} cfg={cfg} m={m} lockedSubs={lockedSubs} />
      </div>

      {/* Roster lock indicator */}
      <Card className="mt-4 p-4 bg-[#11241b] border-white/10">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Substitution Window</div>
            <div className="text-white">
              {periodElapsed < cfg.lockin_seconds ? `🔒 LOCK-IN ZONE · subs locked until ${fmtTime(cfg.lockin_seconds)}` :
                periodElapsed >= cfg.tapout_cutoff_seconds ? `🔒 ABSOLUTE CUTOFF · finish with players on court` :
                `✓ DYNAMIC TAP-OUT WINDOW · subs open until ${fmtTime(cfg.tapout_cutoff_seconds)}`}
            </div>
          </div>
          <div className="text-xs text-zinc-500">Period elapsed: {fmtTime(periodElapsed)}</div>
        </div>
      </Card>

      {/* Active player picker */}
      <Card className="mt-4 p-4 bg-[#11241b] border-white/10" data-testid="active-player-picker">
        <div className="flex items-center gap-2 mb-3">
          <Users className="w-4 h-4 text-[#00A86B]" />
          <span className="font-anton text-xl uppercase tracking-tight text-white">On-Court Players</span>
          <span className="text-xs text-zinc-500">Toggle to sub players in/out. Minutes auto-tracked.</span>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <RosterPicker side="a" team={m.team_a} roster={teamRosters.a} active={m.active_players_a || []} matchId={matchId} />
          <RosterPicker side="b" team={m.team_b} roster={teamRosters.b} active={m.active_players_b || []} matchId={matchId} />
        </div>
      </Card>

      {/* Seed lineup setup (Seed 1/2/3 pairings) */}
      <Card className="mt-4 p-4 bg-[#11241b] border-white/10" data-testid="seed-lineup-setup">
        <div className="flex items-center gap-2 mb-1">
          <Star className="w-4 h-4 text-[#00A86B]" />
          <span className="font-anton text-xl uppercase tracking-tight text-white">Seed Pairings</span>
          <span className="text-xs text-zinc-500">Assign Seed 1/2/3 pairs — they auto-take the court at each period start.</span>
        </div>
        <div className="text-xs text-amber-400/90 mb-3">
          Mandated this period: <span className="font-bold">Seed {m.current_period}</span> — auto-loaded &amp; locked during the first {Math.round((cfg.lockin_seconds || 900) / 60)} min.
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <SeedLineupCard side="a" team={m.team_a} roster={teamRosters.a} lineup={m.seed_lineup_a} numPeriods={cfg.num_periods} matchId={matchId} />
          <SeedLineupCard side="b" team={m.team_b} roster={teamRosters.b} lineup={m.seed_lineup_b} numPeriods={cfg.num_periods} matchId={matchId} />
        </div>
      </Card>

      <MatchCommentary matchId={matchId} canPost={true} />
    </div>
  );
}

function SeedLineupCard({ side, team, roster, lineup, numPeriods, matchId }) {
  const [draft, setDraft] = useState(lineup || { "1": [], "2": [], "3": [] });
  useEffect(() => { setDraft(lineup || { "1": [], "2": [], "3": [] }); }, [lineup]);

  const seedOf = (pid) => Object.keys(draft).find((s) => (draft[s] || []).includes(pid));

  const toggle = (period, pid) => {
    setDraft((d) => {
      const copy = {};
      for (const k of Object.keys(d)) copy[k] = [...(d[k] || [])];
      const cur = copy[period] || [];
      if (cur.includes(pid)) {
        copy[period] = cur.filter((x) => x !== pid);
      } else {
        // remove from any other seed first
        for (const k of Object.keys(copy)) copy[k] = copy[k].filter((x) => x !== pid);
        if ((copy[period] || []).length >= 2) { toast.error("Each seed pairing holds max 2 players"); return d; }
        copy[period] = [...(copy[period] || []), pid];
      }
      return copy;
    });
  };

  const save = async () => {
    try {
      await api.put(`/matches/${matchId}/seed-lineup`, { team: side, lineup: draft });
      toast.success(`Seed lineup saved for ${team?.name || "Team " + side.toUpperCase()}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const periods = Array.from({ length: numPeriods || 3 }, (_, i) => String(i + 1));
  return (
    <div data-testid={`seed-lineup-${side}`} className="rounded-lg border border-white/10 bg-[#060d09] p-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Team {side.toUpperCase()} · {team?.name || ""}</div>
      {periods.map((p) => (
        <div key={p} className="mb-2">
          <div className="text-xs text-[#00A86B] font-bold mb-1">Seed {p} pairing — Period {p}</div>
          <div className="flex flex-wrap gap-1.5">
            {roster.map((pl) => {
              const inThis = (draft[p] || []).includes(pl.id);
              const inOther = !inThis && seedOf(pl.id);
              return (
                <button key={pl.id} data-testid={`seed-${side}-${p}-${pl.id}`} onClick={() => toggle(p, pl.id)}
                  disabled={!!inOther}
                  className={`px-2 py-1 rounded-md border text-xs ${inThis ? "border-[#00A86B] bg-[#00A86B]/15 text-[#00A86B]" : inOther ? "border-white/5 bg-[#0b1611] text-zinc-700 cursor-not-allowed" : "border-white/10 bg-[#11241b] text-zinc-300"}`}>
                  {pl.name}{inOther && ` (S${inOther})`}
                </button>
              );
            })}
            {roster.length === 0 && <span className="text-xs text-zinc-600">No roster players.</span>}
          </div>
        </div>
      ))}
      <Button data-testid={`seed-save-${side}`} size="sm" onClick={save} className="mt-1 bg-[#00A86B] hover:bg-[#00935A] text-white">Save Seed Lineup</Button>
    </div>
  );
}

function RosterPicker({ side, team, roster, active, matchId }) {
  const toggle = async (pid) => {
    const next = active.includes(pid) ? active.filter((x) => x !== pid) : [...active, pid];
    try {
      await api.post(`/matches/${matchId}/active-players`, { team: side, player_ids: next });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div data-testid={`roster-picker-${side}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Team {side.toUpperCase()} · {team?.name || ""}</div>
      <div className="flex flex-wrap gap-1.5">
        {roster.map((p) => {
          const on = active.includes(p.id);
          return (
            <button key={p.id} data-testid={`roster-toggle-${side}-${p.id}`} onClick={() => toggle(p.id)}
              className={`flex items-center gap-2 px-2 py-1 rounded-md border text-xs ${on ? "border-[#00A86B] bg-[#00A86B]/15 text-[#00A86B]" : "border-white/10 bg-[#060d09] text-zinc-400"}`}>
              <span className="w-6 h-6 rounded-full overflow-hidden bg-zinc-800 shrink-0">
                {p.photo_path && <img src={photoUrl(p.photo_path)} className="w-full h-full object-cover" />}
              </span>
              <span className="truncate max-w-[100px]">{p.name}</span>
              {on && <span className="text-[10px] uppercase font-bold">ON</span>}
            </button>
          );
        })}
        {roster.length === 0 && <div className="text-xs text-zinc-600">No players in this team yet.</div>}
      </div>
    </div>
  );
}

function Stat({ label, value, accent, danger, amber }) {
  return (
    <div className={`p-4 rounded-xl border ${accent ? "border-[#00A86B]/40 bg-[#00A86B]/5" : "border-white/10 bg-[#11241b]"}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-mono-score text-3xl mt-1 ${danger ? "text-red-500" : amber ? "text-amber-400" : accent ? "text-[#00A86B]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function TeamControls({ side, team, score, act, cfg, m, lockedSubs }) {
  const goldenLeft = (cfg.golden_buttons_per_period || 1) - (m[`golden_used_${side}`]?.[m.current_period] || 0);
  return (
    <Card className="p-5 bg-[#11241b] border-white/10" data-testid={`umpire-team-${side}`}>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Team {side.toUpperCase()}</div>
          <div className="font-anton text-3xl uppercase tracking-tight text-white truncate">{team.name}</div>
        </div>
        <div className="font-mono-score text-6xl text-white" data-testid={`umpire-score-${side}`}>{score}</div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Button
          data-testid={`btn-plus-${side}`}
          className="h-20 text-2xl bg-[#00A86B] hover:bg-[#00935A] text-white font-bold"
          onClick={() => act("point", { team: side, value: 1 })}
        >
          <Plus className="w-6 h-6 mr-2" /> +1 POINT
        </Button>
        <Button
          data-testid={`btn-minus-${side}`}
          variant="outline"
          className="h-20 text-xl border-white/20 text-white"
          onClick={() => act("point", { team: side, value: -1 })}
        >
          <Minus className="w-6 h-6 mr-2" /> -1
        </Button>
        <Button
          data-testid={`btn-golden-${side}`}
          disabled={goldenLeft <= 0 || m[`golden_pending_${side}`]}
          onClick={() => act("golden", { team: side })}
          className="h-16 bg-amber-500 hover:bg-amber-400 text-black font-bold uppercase tracking-widest disabled:opacity-40"
        >
          <Star className="w-5 h-5 mr-1.5" />
          {m[`golden_pending_${side}`] ? "Golden Armed" : `Golden Point (${goldenLeft} left)`}
        </Button>
        <Button
          data-testid={`btn-tapout-${side}`}
          disabled={lockedSubs || m.tapout_active}
          onClick={() => act("tapout", { team: side })}
          variant="outline"
          className="h-16 border-amber-500/40 text-amber-400 hover:bg-amber-500/10 uppercase tracking-widest disabled:opacity-30"
        >
          <Hand className="w-5 h-5 mr-1.5" /> Tap-Out
        </Button>
      </div>
      {m.tapout_active && m.tapout_team === side && (
        <Button data-testid={`btn-end-tapout-${side}`} variant="outline" className="mt-3 w-full border-amber-500/40 text-amber-400" onClick={() => act("tapout/end")}>
          End Tap-Out Early
        </Button>
      )}
    </Card>
  );
}
